import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Box, styled, keyframes } from "@mui/material";
import ChooseYourPlan from "../../components/Common/ChooseYourPlan";
import {
  HeroSection,
  PartnersSection,
  BecomePartnerSection,
  EnjoyFreshlyPreparedSection,
  NotSureWhereToStartSection,
  FAQSection,
  CustomerReviewsSection,
  MealsSection,
  EnhancedHeroSection,
} from "../../components/Common";
import { useTextAnimation } from "../../components/Common/TextAnimations";
import { Navigate, useNavigate } from "react-router-dom";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { checkEmptyObj, checkIf } from "../../utils";
import { useMediaQuery } from "@react-hook/media-query";

// Keyframe animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const textGlow = keyframes`
  0%, 100% {
    text-shadow: 0 0 5px rgba(41, 188, 98, 0.3);
  }
  50% {
    text-shadow: 0 0 20px rgba(41, 188, 98, 0.8), 0 0 30px rgba(41, 188, 98, 0.4);
  }
`;

const textSlideUp = keyframes`
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const MainContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  overflow: "hidden",
  animation: `${fadeIn} 0.8s ease-out`,
  [theme.breakpoints.down("sm")]: {
    marginTop: "60px", // Static header height
    marginBottom: "60px", // Static footer height
    minHeight: "calc(100vh - 130px)", // Adjust min height to account for header + footer
  },
}));

interface AnimatedSectionProps {
  isVisible: boolean;
  animationType: "fadeInUp" | "slideInFromLeft" | "slideInFromRight" | "scaleIn";
  delay?: number;
  children: React.ReactNode;
  enableTextAnimation?: boolean;
  textAnimationType?: "glow" | "slideUp" | "stagger";
}

const AnimatedSection = styled(Box, {
  shouldForwardProp: (prop) =>
    !["isVisible", "animationType", "enableTextAnimation", "textAnimationType"].includes(prop as string),
})<Omit<AnimatedSectionProps, "children">>(
  ({ isVisible, animationType, delay = 0, enableTextAnimation = false, textAnimationType = "slideUp" }) => {
    const animations = {
      fadeInUp,
      slideInFromLeft,
      slideInFromRight,
      scaleIn,
    };

    const textAnimations = {
      glow: textGlow,
      slideUp: textSlideUp,
      stagger: textSlideUp,
    };

    return {
      // Initial state when not visible (no transition, instant)
      ...(!isVisible && {
        opacity: 0,
        transform: "translateY(20px)",
      }),
      // Keyframe animation handles the entrance — no competing transition
      animation: isVisible ? `${animations[animationType]} 0.8s ease-out ${delay}s both` : "none",
      willChange: "transform, opacity",

      "&:hover": {
        transform: isVisible ? "translateY(-2px)" : "none",
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      },

      // Enhanced text animations
      ...(enableTextAnimation &&
        isVisible && {
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            animation: `${textAnimations[textAnimationType]} 1s ease-out ${delay + 0.3}s both`,
          },
          "& p": {
            animation: `${textAnimations[textAnimationType]} 1s ease-out ${delay + 0.5}s both`,
          },
          "& span": {
            animation: `${textAnimations[textAnimationType]} 1s ease-out ${delay + 0.4}s both`,
          },
        }),
    };
  }
);

const InteractiveSection = styled(Box)(() => ({
  position: "relative",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  willChange: "transform",

  "&:hover": {
    transform: "translateY(-1px)",
    filter: "brightness(1.02)",
  },

  "&:active": {
    transform: "translateY(0px)",
    transition: "transform 0.1s ease-out",
  },
}));

const Homepage: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const mobile = useMediaQuery("only screen and (max-width: 600px)");

  const userHasActiveSubscription = useMemo(() => {
    return checkIf(user.activeSubscription);
  }, [user.activeSubscription]);

  const homepageV2Display = useMemo(() => {
    return !checkEmptyObj(user) && !userHasActiveSubscription;
  }, [user, userHasActiveSubscription]);

  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  const shouldRedirect = !checkEmptyObj(user) && userHasActiveSubscription && !mobile;

  // Custom hook for intersection observer
  const useIntersectionObserver = useCallback(() => {
    useEffect(() => {
      if (shouldRedirect) return; // Don't set up observer if redirecting

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const sectionId = entry.target.getAttribute("data-section-id");
            if (sectionId) {
              setVisibleSections((prev) => {
                const newSet = new Set(prev);
                if (entry.isIntersecting) {
                  newSet.add(sectionId);
                } else {
                  // Keep sections visible once they've been seen for better UX
                  // newSet.delete(sectionId);
                }
                return newSet;
              });
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: "50px 0px -100px 0px",
        }
      );

      // Observe all sections
      sectionRefs.current.forEach((element) => {
        observer.observe(element);
      });

      return () => {
        sectionRefs.current.forEach((element) => {
          observer.unobserve(element);
        });
      };
    }, [shouldRedirect]);
  }, [shouldRedirect]);

  // Initialize page load animation
  useEffect(() => {
    if (shouldRedirect) return; // Don't animate if redirecting

    const timer = setTimeout(() => {
      setIsPageLoaded(true);
    }, 100);

    return () => {
      clearTimeout(timer);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };
  }, [shouldRedirect]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate("/meal-plan");
    }
  }, [navigate, shouldRedirect]);

  // Helper function to register section refs
  const setSectionRef = useCallback(
    (sectionId: string) => (el: HTMLElement | null) => {
      if (el) {
        sectionRefs.current.set(sectionId, el);
      } else {
        sectionRefs.current.delete(sectionId);
      }
    },
    []
  );

  // Set up intersection observer - MUST be called before any early returns
  useIntersectionObserver();

  // Return null if user should be redirected
  if (shouldRedirect) {
    return null;
  }

  return (
    <MainContainer>
      {homepageV2Display ? (
        // Homepage V2 Layout - Specific order and limited sections
        <>
          {/* 1. Hero Section */}
          <Box ref={setSectionRef("hero")} data-section-id="hero">
            <AnimatedSection
              isVisible={isPageLoaded}
              animationType="fadeInUp"
              delay={0.1}
              enableTextAnimation={true}
              textAnimationType="slideUp"
            >
              <EnhancedHeroSection animationVariant="shimmer" />
            </AnimatedSection>
          </Box>

          {/* 2. Meal Cards Section */}
          <InteractiveSection ref={setSectionRef("meals")} data-section-id="meals">
            <AnimatedSection
              isVisible={visibleSections.has("meals")}
              animationType="scaleIn"
              delay={0.1}
              enableTextAnimation={false}
              textAnimationType="slideUp"
            >
              <MealsSection />
            </AnimatedSection>
          </InteractiveSection>

          {/* 3. Why People Choose Us Section */}
          <InteractiveSection ref={setSectionRef("choose-plan")} data-section-id="choose-plan">
            <AnimatedSection
              isVisible={visibleSections.has("choose-plan")}
              animationType="slideInFromLeft"
              delay={0.1}
              enableTextAnimation={true}
              textAnimationType="stagger"
            >
              <ChooseYourPlan />
            </AnimatedSection>
          </InteractiveSection>

          {/* 4. Not Sure Where to Start Section */}
          <InteractiveSection ref={setSectionRef("not-sure")} data-section-id="not-sure">
            <NotSureWhereToStartSection
              leftContentProps={{
                isVisible: visibleSections.has("not-sure"),
                enableTextAnimation: true,
                delay: 0.1,
              }}
              rightContentProps={{
                isVisible: visibleSections.has("not-sure"),
                enableAnimation: true,
                delay: 0.3,
              }}
            />
          </InteractiveSection>

          {/* 5. Partners Section */}
          <InteractiveSection ref={setSectionRef("partners")} data-section-id="partners">
            <AnimatedSection
              isVisible={visibleSections.has("partners")}
              animationType="fadeInUp"
              delay={0.1}
              enableTextAnimation={true}
              textAnimationType="stagger"
            >
              <PartnersSection />
            </AnimatedSection>
          </InteractiveSection>
        </>
      ) : (
        // Original Homepage Layout - All sections in original order
        <>
          {/* Hero Section */}
          <Box ref={setSectionRef("hero")} data-section-id="hero">
            <AnimatedSection
              isVisible={isPageLoaded}
              animationType="fadeInUp"
              delay={0.1}
              enableTextAnimation={true}
              textAnimationType="slideUp"
            >
              <EnhancedHeroSection animationVariant="shimmer" />
            </AnimatedSection>
          </Box>

          {/* Why People Choose Us Section - New Figma Design */}
          <InteractiveSection ref={setSectionRef("choose-plan")} data-section-id="choose-plan">
            <AnimatedSection
              isVisible={visibleSections.has("choose-plan")}
              animationType="slideInFromLeft"
              delay={0.1}
              enableTextAnimation={true}
              textAnimationType="stagger"
            >
              <ChooseYourPlan />
            </AnimatedSection>
          </InteractiveSection>

          {/* Meal Cards Section */}
          <InteractiveSection ref={setSectionRef("meals")} data-section-id="meals">
            <AnimatedSection
              isVisible={visibleSections.has("meals")}
              animationType="scaleIn"
              delay={0.1}
              enableTextAnimation={false}
              textAnimationType="slideUp"
            >
              <MealsSection />
            </AnimatedSection>
          </InteractiveSection>

          {/* Enjoy freshly prepared section */}
          <InteractiveSection ref={setSectionRef("freshly-prepared")} data-section-id="freshly-prepared">
            <AnimatedSection
              isVisible={visibleSections.has("freshly-prepared")}
              animationType="slideInFromRight"
              delay={0.1}
              enableTextAnimation={true}
              textAnimationType="stagger"
            >
              <EnjoyFreshlyPreparedSection />
            </AnimatedSection>
          </InteractiveSection>

          {/* Not Sure Where to Start Section */}
          <InteractiveSection ref={setSectionRef("not-sure")} data-section-id="not-sure">
            <NotSureWhereToStartSection
              leftContentProps={{
                isVisible: visibleSections.has("not-sure"),
                enableTextAnimation: true,
                delay: 0.1,
              }}
              rightContentProps={{
                isVisible: visibleSections.has("not-sure"),
                enableAnimation: true,
                delay: 0.3,
              }}
            />
          </InteractiveSection>

          {/* FAQ Section */}
          <InteractiveSection ref={setSectionRef("faq")} data-section-id="faq">
            <AnimatedSection
              isVisible={visibleSections.has("faq")}
              animationType="slideInFromLeft"
              delay={0.1}
              enableTextAnimation={true}
              textAnimationType="stagger"
            >
              <FAQSection />
            </AnimatedSection>
          </InteractiveSection>

          {/* Customer Reviews Section */}
          <InteractiveSection ref={setSectionRef("reviews")} data-section-id="reviews">
            <AnimatedSection
              isVisible={visibleSections.has("reviews")}
              animationType="scaleIn"
              delay={0.1}
              enableTextAnimation={true}
              textAnimationType="stagger"
            >
              <CustomerReviewsSection />
            </AnimatedSection>
          </InteractiveSection>

          {/* Become a Partner Section */}
          <InteractiveSection ref={setSectionRef("become-partner")} data-section-id="become-partner">
            <AnimatedSection
              isVisible={visibleSections.has("become-partner")}
              animationType="slideInFromRight"
              delay={0.1}
              enableTextAnimation={true}
              textAnimationType="stagger"
            >
              <BecomePartnerSection />
            </AnimatedSection>
          </InteractiveSection>

          {/* Partners Section */}
          <InteractiveSection ref={setSectionRef("partners")} data-section-id="partners">
            <AnimatedSection
              isVisible={visibleSections.has("partners")}
              animationType="fadeInUp"
              delay={0.1}
              enableTextAnimation={true}
              textAnimationType="stagger"
            >
              <PartnersSection />
            </AnimatedSection>
          </InteractiveSection>
        </>
      )}
    </MainContainer>
  );
};

export default Homepage;
